/**
 * MessagesPage — In-app messaging with split-view inbox (Epic 5)
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, MessageSquare, Send, Plus } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import {
  listConversations,
  getMessages,
  sendMessage,
  createOrGetConversation,
  type Conversation,
  type Message,
} from '../api/messagesApi'
import { getMyConnections, type Connection } from '../api/brokerApi'

function formatTime(iso: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  if (isToday) {
    return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
  }
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

function formatMessageTime(iso: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
}

export default function MessagesPage() {
  const { getAccessToken, user } = useAuth()
  const navigate = useNavigate()

  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loadingConversations, setLoadingConversations] = useState(true)
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [messageText, setMessageText] = useState('')
  const [sending, setSending] = useState(false)
  // On mobile, toggle between list and thread
  const [mobileView, setMobileView] = useState<'list' | 'thread'>('list')
  // New conversation flow
  const [showNewConv, setShowNewConv] = useState(false)
  const [connections, setConnections] = useState<Connection[]>([])
  const [loadingConnections, setLoadingConnections] = useState(false)
  const [creatingConv, setCreatingConv] = useState(false)

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const threadBottomRef = useRef<HTMLDivElement | null>(null)

  const token = getAccessToken()

  // ── Load conversations ──────────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    if (!token) return
    try {
      const res = await listConversations(token)
      setConversations(res.conversations)
    } catch {
      // silent — empty state shown
    } finally {
      setLoadingConversations(false)
    }
  }, [token])

  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  // ── Load messages for selected conversation ─────────────────────────────────
  const loadMessages = useCallback(
    async (convId: string, page = 1, prepend = false) => {
      if (!token) return
      if (!prepend) setLoadingMessages(true)
      try {
        const res = await getMessages(token, convId, page)
        if (prepend) {
          setMessages(prev => [...res.messages, ...prev])
        } else {
          setMessages(res.messages)
        }
        setHasMore(res.has_more)
        setCurrentPage(page)
      } catch {
        // silent
      } finally {
        setLoadingMessages(false)
      }
    },
    [token]
  )

  // ── Select conversation ─────────────────────────────────────────────────────
  const handleSelectConversation = useCallback(
    async (conv: Conversation) => {
      setSelectedConv(conv)
      setMessages([])
      setHasMore(false)
      setCurrentPage(1)
      setMobileView('thread')
      await loadMessages(conv.id)
    },
    [loadMessages]
  )

  // ── Polling for new messages ────────────────────────────────────────────────
  useEffect(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    if (!selectedConv) return

    pollTimerRef.current = setInterval(async () => {
      if (!token || !selectedConv) return
      try {
        const res = await getMessages(token, selectedConv.id, 1)
        setMessages(res.messages)
        setHasMore(res.has_more)
      } catch {
        // silent
      }
    }, 15000)

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    }
  }, [selectedConv, token])

  // ── Auto-scroll to bottom when messages load ───────────────────────────────
  useEffect(() => {
    if (threadBottomRef.current) {
      threadBottomRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages.length])

  // ── Load more (older) messages ──────────────────────────────────────────────
  const handleLoadMore = async () => {
    if (!selectedConv || !hasMore || loadingMore) return
    setLoadingMore(true)
    const nextPage = currentPage + 1
    await loadMessages(selectedConv.id, nextPage, true)
    setLoadingMore(false)
  }

  // ── Send message ────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!token || !selectedConv || !messageText.trim() || sending) return
    const text = messageText.trim()
    setSending(true)
    setMessageText('')
    try {
      const msg = await sendMessage(token, selectedConv.id, text)
      setMessages(prev => [...prev, msg])
      // Refresh conversation list to update preview
      loadConversations()
    } catch {
      // Restore text on failure
      setMessageText(text)
      alert('Failed to send message. Please try again.')
    } finally {
      setSending(false)
    }
  }

  // ── New conversation flow ──────────────────────────────────────────────────
  const handleNewConversation = async () => {
    if (!token) return
    setShowNewConv(true)
    setLoadingConnections(true)
    try {
      const res = await getMyConnections(token)
      // Filter to active connections only
      const active = (res.connections || []).filter((c: Connection) => c.status === 'active')
      setConnections(active)
    } catch {
      setConnections([])
    } finally {
      setLoadingConnections(false)
    }
  }

  const handleStartConversation = async (conn: Connection) => {
    if (!token || creatingConv) return
    setCreatingConv(true)
    try {
      // Determine the other user based on caller role
      const otherUserId = user?.role === 'customer' ? conn.broker_id : conn.customer_id
      const conv = await createOrGetConversation(token, { other_user_id: otherUserId })
      setShowNewConv(false)
      // Reload conversations and select the new one
      await loadConversations()
      // Build a conversation-like object to select
      const fullConv: Conversation = {
        id: conv.id,
        customer_id: conv.customer_id,
        broker_id: conv.broker_id,
        last_message_at: null,
        created_at: conv.created_at || null,
        other_party_name: user?.role === 'customer' ? (conn.broker_name || conn.customer_email || 'Broker') : (conn.customer_name || conn.customer_email || 'Customer'),
        last_message_preview: null,
        unread_count: 0,
      }
      handleSelectConversation(fullConv)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start conversation')
    } finally {
      setCreatingConv(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b flex-shrink-0">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            {/* On mobile inside thread, show back-to-list; otherwise navigate(-1) */}
            {mobileView === 'thread' ? (
              <button
                className="text-gray-500 hover:text-gray-700 md:hidden"
                onClick={() => setMobileView('list')}
              >
                <ArrowLeft size={20} />
              </button>
            ) : null}
            <button
              onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-gray-700 hidden md:block"
            >
              <ArrowLeft size={20} />
            </button>
            <button
              onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-gray-700 md:hidden"
              style={{ display: mobileView === 'thread' ? 'none' : undefined }}
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <MessageSquare size={22} className="text-blue-600" />
                Messages
              </h1>
              {selectedConv && mobileView === 'thread' && (
                <p className="text-sm text-gray-500 md:hidden">
                  {selectedConv.other_party_name}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content — split view */}
      <div className="flex flex-1 overflow-hidden max-w-6xl w-full mx-auto">
        {/* ── Conversation list ─────────────────────────────────────────────── */}
        <div
          className={`w-full md:w-80 border-r bg-white flex-shrink-0 flex flex-col overflow-hidden ${
            mobileView === 'thread' ? 'hidden md:flex' : 'flex'
          }`}
        >
          {/* New conversation button */}
          <div className="px-3 py-2 border-b flex-shrink-0">
            <button
              onClick={handleNewConversation}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <Plus size={16} />
              New Conversation
            </button>
          </div>

          {/* New conversation picker modal */}
          {showNewConv && (
            <div className="px-3 py-3 border-b bg-blue-50 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-700">Select a connection:</p>
                <button onClick={() => setShowNewConv(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
              </div>
              {loadingConnections ? (
                <p className="text-xs text-gray-500">Loading connections...</p>
              ) : connections.length === 0 ? (
                <p className="text-xs text-gray-500">No active connections. Connect with a {user?.role === 'customer' ? 'broker' : 'customer'} first.</p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {connections.map(conn => (
                    <button
                      key={conn.id}
                      onClick={() => handleStartConversation(conn)}
                      disabled={creatingConv}
                      className="w-full text-left px-3 py-2 text-sm bg-white rounded-lg border hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      {user?.role === 'customer'
                        ? (conn.broker_name || 'Broker')
                        : (conn.customer_name || conn.customer_email || 'Customer')}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto divide-y">
            {loadingConversations ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                Loading conversations...
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare size={40} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">No conversations yet</p>
              </div>
            ) : (
              conversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3 ${
                    selectedConv?.id === conv.id ? 'bg-blue-50 border-l-2 border-l-blue-600' : ''
                  }`}
                >
                  {/* Avatar placeholder */}
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-sm flex-shrink-0 mt-0.5">
                    {(conv.other_party_name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {conv.other_party_name}
                      </span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {formatTime(conv.last_message_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <p className="text-xs text-gray-500 truncate flex-1">
                        {conv.last_message_preview || 'No messages yet'}
                      </p>
                      {conv.unread_count > 0 && (
                        <span className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ── Message thread ────────────────────────────────────────────────── */}
        <div
          className={`flex-1 flex flex-col overflow-hidden bg-gray-50 ${
            mobileView === 'list' ? 'hidden md:flex' : 'flex'
          }`}
        >
          {!selectedConv ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <MessageSquare size={48} className="text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                Select a conversation to start messaging
              </h3>
              <p className="text-sm text-gray-500">
                Choose a conversation from the list on the left
              </p>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="bg-white border-b px-4 py-3 flex-shrink-0">
                <p className="font-semibold text-gray-900">
                  {selectedConv.other_party_name}
                </p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-2">
                {/* Load more button */}
                {hasMore && (
                  <div className="flex justify-center mb-2">
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="text-xs text-blue-600 hover:underline disabled:text-gray-400"
                    >
                      {loadingMore ? 'Loading...' : 'Load older messages'}
                    </button>
                  </div>
                )}

                {loadingMessages ? (
                  <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
                    Loading messages...
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                    No messages yet. Say hello!
                  </div>
                ) : (
                  messages.map(msg => {
                    const isOwn = msg.sender_id === user?.id
                    const isSystem = msg.message_type === 'system'

                    if (isSystem) {
                      return (
                        <div key={msg.id} className="flex justify-center my-1">
                          <span className="text-xs text-gray-400 italic bg-gray-100 px-3 py-1 rounded-full">
                            {msg.content}
                          </span>
                        </div>
                      )
                    }

                    return (
                      <div
                        key={msg.id}
                        className={`flex flex-col gap-0.5 ${isOwn ? 'items-end' : 'items-start'}`}
                      >
                        <div
                          className={`max-w-xs md:max-w-md lg:max-w-lg px-3 py-2 rounded-2xl text-sm break-words whitespace-pre-wrap ${
                            isOwn
                              ? 'bg-blue-600 text-white rounded-br-sm'
                              : 'bg-white text-gray-900 border rounded-bl-sm'
                          }`}
                        >
                          {msg.content}
                        </div>
                        <span className="text-xs text-gray-400 px-1">
                          {formatMessageTime(msg.created_at)}
                        </span>
                      </div>
                    )
                  })
                )}
                <div ref={threadBottomRef} />
              </div>

              {/* Send form */}
              <div className="bg-white border-t px-4 py-3 flex-shrink-0">
                <div className="flex items-end gap-2">
                  <textarea
                    value={messageText}
                    onChange={e => setMessageText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    maxLength={5000}
                    rows={1}
                    placeholder="Type a message… (Enter to send)"
                    className="flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent max-h-32 overflow-y-auto"
                    style={{ minHeight: '40px' }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!messageText.trim() || sending}
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex-shrink-0 transition-colors"
                    title="Send message"
                  >
                    <Send size={18} />
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1 text-right">
                  {messageText.length}/5000
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
